import React, { useState, useEffect, Fragment }  from 'react';
import { useParams, Link as RouterLink } from "react-router-dom"
import { makeStyles } from '@material-ui/core/styles';
import { DateTime } from 'luxon';

import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Paper from '@material-ui/core/Paper';
import Link from '@material-ui/core/Link';
import Avatar from '@material-ui/core/Avatar';
import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import IconButton from '@material-ui/core/IconButton';

import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ReceiptIcon from '@material-ui/icons/Receipt';
import EditIcon from '@material-ui/icons/Edit';
import SendIcon from '@material-ui/icons/Send';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import PaymentIcon from '@material-ui/icons/Payment';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import DeleteIcon from '@material-ui/icons/Delete';
import DownloadIcon from '@material-ui/icons/CloudDownload';

import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';

import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';

import ChargePaymentMethodDialog from 'app/components/accounting/payments/ChargePaymentMethodDialog';
import EditPaymentDialog from 'app/components/accounting/payments/EditPaymentDialog';
import EditRefundDialog from 'app/components/accounting/refunds/EditRefundDialog';
import Loading from 'app/components/loaders/Loading';
import RedButton from 'app/components/buttons/RedButton';

import { paper } from 'app/styles/core';

import InvoiceStatus from 'app/utils/InvoiceStatus';

import useApi from 'app/context/useApi';
import useApp from 'app/context/useApp';

const useStyles = makeStyles(theme => ({
    container: {
        maxWidth: 980,
        margin: '0 auto',
        minHeight: 460,
        position: 'relative',
        padding: theme.spacing(3)
    },
    marginBottom: {
        marginBottom: theme.spacing(1)
    },
    marginLeft: {
        marginLeft: theme.spacing(3)
    },
    loadingContainer: {
        height: 400,
        position: 'relative'
    },
    paper: {
        ...paper(theme),
        position: 'relative',
        marginBottom: theme.spacing(3)
    },
    avatar: {
        backgroundColor: theme.palette.primary.main,
        color: '#ffffff'
    },
    avatarError: {
        backgroundColor: theme.palette.error.main,
        color: '#ffffff'
    },
    avatarSuccess: {
        backgroundColor: theme.palette.success.main,
        color: '#ffffff'
    },
    flexGrow: {
        flexGrow: 1
    },
    noBorder: {
        border: 'none'
    }

}));

export default function ViewInvoice(props) {

    const [ invoice, setInvoice ] = useState(null);

    const [ editingPayment, setEditingPayment ] = useState(null);

    const [ chargePaymentMethodDialogOpen, setChargePaymentMethodDialogOpen ] = useState(false);
    const [ editPaymentDialogOpen, setEditPaymentDialogOpen ] = useState(false);
    const [ editRefundDialogOpen, setEditRefundDialogOpen ] = useState(false);
    const [ deleteInvoiceDialogOpen, setDeleteInvoiceDialogOpen ] = useState(false);

    const [ emailDialogOpen, setEmailDialogOpen ] = useState(false);
    const [ emailDialogType, setEmailDialogType ] = useState("invoice");
    const [ emailDialogAddress, setEmailDialogAddress ] = useState("");

    const [ loading, setLoading ] = useState(false);

    const api = useApi();
    const { app } = useApp();

    const classes = useStyles();

    let { id } = useParams();

    // GETTERS ------------------------------------------------

    const getInvoice = () => {
        setLoading(true);

        api.get('/admin/accounting/invoice/' + id)
            .then((res) => {
                setInvoice({
                    ...res.invoice,
                    invoice_by: DateTime.fromISO(res.invoice.invoice_by),
                    due_by: DateTime.fromISO(res.invoice.due_by),
                    date_paid: DateTime.fromISO(res.invoice.date_paid),
                    invoice_email_sent_at: DateTime.fromISO(res.invoice.invoice_email_sent_at),
                    late_email_sent_at: DateTime.fromISO(res.invoice.late_email_sent_at),
                    created_at: DateTime.fromISO(res.invoice.created_at),
                });
                setEmailDialogAddress(res.invoice.user.email);
            }).finally(() => setLoading(false));
    }

    useEffect(() => {
        getInvoice();
    }, []);

    // POSTERS ------------------------------------------------

    const postDownloadInvoice = () => {
        setLoading(true);

        api.get('/admin/accounting/invoice/' + id + '/download/invoice', {
            responseType: 'blob',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/pdf'
            }
        }).then(blob => {
                // Create blob link to download
                const url = window.URL.createObjectURL(
                    new Blob([blob]),
                );
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute(
                    'download',
                    `invoice-${invoice.invoice_number}-for-${invoice.user.first}-${invoice.user.last}.pdf`,
                );
            
                // Append to html link element page
                document.body.appendChild(link);
            
                // Start download
                link.click();
            
                // Clean up and remove the link
                link.parentNode.removeChild(link);
            })
            .finally(() => setLoading(false));
    }

    const postSendEmail = () => {
        switch (emailDialogType) {
            case 'invoice':
                postSendInvoice();
                break;
            case 'notice':
                postSendNotice();
                break;
            case 'receipt':
                postSendReceipt();
                break;
        }

        closeEmailDialog();
    }

    const postSendInvoice = () => {
        setLoading(true);

        api.get(`/admin/accounting/invoice/${id}/send/invoice/${emailDialogAddress}`).finally(() => setLoading(false));
    }

    const postSendNotice = () => {
        setLoading(true);

        api.get(`/admin/accounting/invoice/${id}/send/notice/${emailDialogAddress}`).finally(() => setLoading(false));
    }

    const postSendReceipt = () => {
        setLoading(true);

        api.get(`/admin/accounting/invoice/${id}/send/receipts/${emailDialogAddress}`).finally(() => setLoading(false));
    }

    const deleteRefund = id => {
        setLoading(true);

        api.delete('/admin/accounting/refund/' + id)
            .then(() => getInvoice())
            .finally( () => setLoading(false));
    }

    // Email Dialog ----------------------------------------

    const openEmailDialog = type => {
        setEmailDialogOpen(true);
        setEmailDialogType(type);
    }

    const closeEmailDialog = () => {
        setEmailDialogOpen(false);
    }

    const handleEmailDialogAddress = event => {
        setEmailDialogAddress(event.target.value)
    }

    // Charge PaymentMethod Dialog ------------------------------------------------

    const openChargePaymentMethodDialog = () => {
        setChargePaymentMethodDialogOpen(true);
    }

    const closeChargePaymentMethodDialog = () => {
        setChargePaymentMethodDialogOpen(false);
    }

    const closeChargePaymentMethodDialogAndRefresh = () => {
        closeChargePaymentMethodDialog();
        getInvoice();
    }

    // Edit Payment Dialog ------------------------------------------------

    const openEditPaymentDialog = (payment = null) => {
        setEditingPayment(payment);
        setEditPaymentDialogOpen(true);
    }

    const closeEditPaymentDialog = () => {
        setEditPaymentDialogOpen(false);
        setEditingPayment(null);
    }

    const closeEditPaymentDialogAndRefresh = () => {
        closeEditPaymentDialog();
        getInvoice();
    }

    // Refund Payment Dialog ------------------------------------------------

    const openEditRefundDialog = () => {
        setEditRefundDialogOpen(true);
    }

    const closeEditRefundDialog = () => {
        setEditRefundDialogOpen(false);
    }

    const closeEditRefundDialogAndRefresh = () => {
        closeEditRefundDialog();
        getInvoice();
    }

    // Delete Invoice Dialog ----------------------------------

    const openDeleteInvoiceDialog = () => setDeleteInvoiceDialogOpen(true);

    const closeDeleteInvoiceDialog = () => setDeleteInvoiceDialogOpen(false);

    const closeDeleteInvoiceDialogAndRefresh = () => {
        closeDeleteInvoiceDialog();
        getItems();
    }

    const deleteInvoice = () => {

        setLoading(true);

        api.delete('/admin/accounting/invoice/' + id)
            .then(() => closeDeleteInvoiceDialogAndRefresh())
            .finally(() => setLoading(false));
    }

    // CONSTANTS ------------------------------------------------

    const status = InvoiceStatus(invoice);

    return (
        <form className={classes.container} noValidate autoComplete="off">
            <Loading isLoading={loading}/>
            {invoice &&
                <Fragment>
                    <Grid container
                        alignItems="center"
                        justify="space-between"
                        spacing={3}
                        className={classes.marginBottom}
                        >
                        <Grid item>
                            <Typography variant="h4" component="h2">
                                Invoice {invoice.invoice_number}
                            </Typography>
                        </Grid>
                        <Grid item>
                            <Button
                                to="/admin/accounting/invoices"
                                component={RouterLink}
                                startIcon={<ChevronLeftIcon />}
                                >
                                Back to Invoices
                            </Button>
                        </Grid>
                    </Grid>
                    <Paper className={classes.paper}>
                        <Grid container spacing={3} justify="space-between">
                            <Grid item>
                                <Typography component="div" variant="subtitle2" color="textSecondary">
                                    Status
                                </Typography>
                                <Typography component="div" variant="h6" className={status.class}>
                                    {status.label}
                                </Typography>
                            </Grid>
                            <Grid item>
                                <Typography component="div" variant="subtitle2" color="textSecondary">
                                    User
                                </Typography>
                                <Typography component="div" variant="h6">
                                    <Link component={RouterLink} to={"/admin/users/" + invoice.user.id}>
                                        {invoice.user.full_name}
                                    </Link>
                                </Typography>
                            </Grid>
                            <Grid item>
                                <Typography component="div" variant="subtitle2" color="textSecondary">
                                    Invoice Amount
                                </Typography>
                                <Typography component="div" variant="h6">
                                    ${invoice.amount_f}
                                </Typography>
                            </Grid>
                            <Grid item>
                                <Typography component="div" variant="subtitle2" color="textSecondary">
                                    Amount Due
                                </Typography>
                                <Typography component="div" variant="h6">
                                    ${invoice.amount_due_f}
                                </Typography>
                            </Grid>
                            <Grid item>
                                <Typography component="div" variant="subtitle2" color="textSecondary">
                                    Due On
                                </Typography>
                                <Typography component="div" variant="h6">
                                    {invoice.due_by.toLocaleString(DateTime.DATE_MED)}
                                </Typography>
                            </Grid>
                        </Grid>
                    </Paper>
                    <Paper className={classes.paper}>
                        <Grid container spacing={3}>
                            <Grid item>
                                <Avatar className={classes.avatar}>
                                    <ReceiptIcon />
                                </Avatar>
                            </Grid>
                            <Grid item className={classes.flexGrow}>
                                <Typography component="div" variant="h6">
                                    Invoice Created
                                </Typography>
                                <Typography component="div" variant="subtitle1">
                                    <Typography component="span" color="textSecondary">
                                        Invoiced On:&nbsp;
                                    </Typography>
                                    {invoice.invoice_by.toLocaleString(DateTime.DATE_MED)}
                                </Typography>
                            </Grid>
                            <Grid item>
                                <Button
                                    aria-label="Edit Invoice"
                                    startIcon={<EditIcon />}
                                    component={RouterLink}
                                    to={"/admin/accounting/invoices/" + invoice.id}
                                    variant="outlined"
                                    color="primary"
                                    >
                                    Edit Invoice
                                </Button>
                                <RedButton
                                    aria-label="Delete Invoice"
                                    onClick={openDeleteInvoiceDialog}
                                    startIcon={<DeleteIcon />}
                                    variant="outlined"
                                    className={classes.marginLeft}
                                    >
                                    Delete Invoice
                            </RedButton>
                            </Grid>
                        </Grid>
                        <Typography align="right" component="div" variant="subtitle2" color="textSecondary" className={classes.marginBottom}>
                            Description: {invoice.description}
                        </Typography>
                        <Typography component="div" variant="subtitle1">
                            Invoice Items
                        </Typography>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Item</TableCell>
                                    <TableCell>Description</TableCell>
                                    <TableCell align="right">Price</TableCell>
                                    <TableCell align="right">Quantity</TableCell>
                                    <TableCell align="right">Total</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {invoice.invoice_items.map(ii => (
                                    <TableRow key={ii.id}>
                                        <TableCell>
                                            {ii.name}
                                        </TableCell>
                                        <TableCell>
                                            {ii.description}
                                        </TableCell>
                                        <TableCell align="right">${ii.amount}</TableCell>
                                        <TableCell align="right">{ii.quantity}</TableCell>
                                        <TableCell align="right">${(ii.quantity * ii.amount).toFixed(2)}</TableCell>
                                    </TableRow>
                                ))}
                                <TableRow>
                                    <TableCell component="th" scope="row" colSpan={4} className={classes.noBorder}>
                                        <Typography variant="subtitle2" align="right">
                                            Invoice Total:
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="right" className={classes.noBorder}>
                                        <Typography color="primary" variant="subtitle2">
                                            ${invoice.amount_f}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                        <Typography component="div" variant="caption">
                            Reporting Category: {(invoice.reporting_category && invoice.reporting_category != '') ? invoice.reporting_category : 'n/a'}
                        </Typography>
                    </Paper>
                    <Paper className={classes.paper}>
                        <Grid container spacing={3}>
                            <Grid item>
                                <Avatar className={classes.avatar}>
                                    <SendIcon />
                                </Avatar>
                            </Grid>
                            <Grid item className={classes.flexGrow}>
                                <Typography component="div" variant="h6">
                                    Send Invoice
                                </Typography>
                                <Typography component="div" variant="subtitle1">
                                    <Typography component="span" color="textSecondary">
                                        Invoice Last Emailed On:&nbsp;
                                    </Typography>
                                    {invoice.invoice_email_sent_at.isValid ? (
                                        <span>{invoice.invoice_email_sent_at.toLocaleString(DateTime.DATE_MED)}</span>
                                    ) : (
                                        <span>Never</span>
                                    )}
                                </Typography>
                            </Grid>
                            <Grid item>
                                <Button
                                    aria-label="Send Invoice"
                                    startIcon={<SendIcon />}
                                    onClick={() => openEmailDialog('invoice')}
                                    variant="contained"
                                    color="primary"
                                    >
                                    Send Invoice
                                </Button>
                                <Button
                                    aria-label="Download Invoice"
                                    startIcon={<DownloadIcon />}
                                    onClick={postDownloadInvoice}
                                    variant="outlined"
                                    color="primary"
                                    className={classes.marginLeft}
                                    >
                                    Download Invoice
                                </Button>
                            </Grid>
                        </Grid>
                    </Paper>
                    {(status.id === 'late') &&
                        <Paper className={classes.paper}>
                            <Grid container spacing={3}>
                                <Grid item>
                                    <Avatar className={classes.avatarError}>
                                        <ErrorOutlineIcon />
                                    </Avatar>
                                </Grid>
                                <Grid item className={classes.flexGrow}>
                                    <Typography component="div" variant="h6">
                                        Invoice is Past Due
                                    </Typography>
                                    <Typography component="div" variant="subtitle1">
                                        <Typography component="span" color="textSecondary">
                                            Past Due Notice Emailed On:&nbsp;
                                        </Typography>
                                        {invoice.late_email_sent_at.isValid ? (
                                            <span>{invoice.late_email_sent_at.toLocaleString(DateTime.DATE_MED)}</span>
                                        ) : (
                                            <span>Never</span>
                                        )}
                                    </Typography>
                                </Grid>
                                <Grid item>
                                    <Button
                                        aria-label="Send Notice"
                                        startIcon={<SendIcon />}
                                        onClick={() => openEmailDialog('notice')}
                                        variant="outlined"
                                        color="primary"
                                        >
                                        Send Notice
                                    </Button>
                                </Grid>
                            </Grid>
                        </Paper>
                    }
                    <Paper className={classes.paper}>
                        <Grid container spacing={3}>
                            <Grid item>
                                <Avatar className={classes.avatar}>
                                    <PaymentIcon />
                                </Avatar>
                            </Grid>
                            <Grid item className={classes.flexGrow}>
                                <Typography component="div" variant="h6">
                                    Record a Payment
                                </Typography>
                                <Typography component="div" variant="subtitle1">
                                    <Typography component="span" color="textSecondary">
                                        Amount Due:&nbsp;
                                    </Typography>
                                    ${invoice.amount_due_f}
                                </Typography>
                            </Grid>
                            <Grid item>
                                <Button
                                    aria-label="Charge a Payment Method"
                                    variant="contained"
                                    color="primary"
                                    onClick={openChargePaymentMethodDialog}
                                    >
                                    Charge a Payment Method
                                </Button>
                                <Button
                                    aria-label="Record a Payment"
                                    variant="outlined"
                                    color="primary"
                                    onClick={() => openEditPaymentDialog(null)}
                                    className={classes.marginLeft}
                                    >
                                    Record a Payment
                                </Button>
                            </Grid>
                        </Grid>
                    </Paper>
                    {(invoice.amount_due <= 0) &&
                        <Paper className={classes.paper}>
                            <Grid container spacing={3}>
                                <Grid item>
                                    <Avatar className={classes.avatarSuccess}>
                                        <CheckCircleOutlineIcon />
                                    </Avatar>
                                </Grid>
                                <Grid item className={classes.flexGrow}>
                                    <Typography component="div" variant="h6">
                                        Invoice has been Paid
                                    </Typography>
                                    <Typography component="div" variant="subtitle1">
                                        <Typography component="span" color="textSecondary">
                                            Paid In Full On:&nbsp;
                                        </Typography>
                                        <span>{invoice.date_paid.toLocaleString(DateTime.DATE_MED)}</span>
                                    </Typography>
                                    <Typography component="div" variant="subtitle1">
                                        {(invoice.refunds.length > 0) &&
                                            <Fragment>
                                                <Typography component="span" color="textSecondary">
                                                    Net Invoice Income (After Refunds):&nbsp;
                                                </Typography>
                                                <span>${invoice.net_amount_f}</span>
                                            </Fragment>
                                        }
                                    </Typography>
                                </Grid>
                                <Grid item>
                                    <Button
                                        aria-label="Send Receipt"
                                        variant="outlined"
                                        color="primary"
                                        onClick={() => openEmailDialog('receipt')}
                                        startIcon={<SendIcon />}
                                        >
                                        Send Receipt
                                    </Button>
                                    <Button
                                        aria-label="Issue a Refund"
                                        variant="contained"
                                        color="primary"
                                        onClick={openEditRefundDialog}
                                        className={classes.marginLeft}
                                        >
                                        Issue a Refund
                                    </Button>
                                </Grid>
                            </Grid>
                        </Paper>
                    }
                    {(invoice.payments.length > 0) &&
                        <Paper className={classes.paper}>
                            <Grid container spacing={3}>
                                <Grid item className={classes.flexGrow}>
                                    <Typography component="div" variant="h6">
                                        Payments
                                    </Typography>
                                </Grid>
                                <Grid item>
                                    <Typography component="div" variant="subtitle1">
                                        <Typography component="span" color="textSecondary">
                                            Total Paid:&nbsp;
                                        </Typography>
                                        ${invoice.amount_paid_f}
                                    </Typography>
                                </Grid>
                            </Grid>
                            <Table aria-label="Previous Payments">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Status</TableCell>
                                        <TableCell>Date</TableCell>
                                        <TableCell align="right">Amount</TableCell>
                                        <TableCell>Paid By</TableCell>
                                        <TableCell>Method</TableCell>
                                        <TableCell>Message</TableCell>
                                        <TableCell>Edit</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {invoice.payments.map((payment) => (
                                        <TableRow key={payment.id}>
                                            <TableCell>
                                                {window.ucFirst(payment.status)}
                                            </TableCell>
                                            <TableCell>
                                                {DateTime.fromISO(payment.paid_at).toLocaleString(DateTime.DATE_MED)}
                                            </TableCell>
                                            <TableCell align="right">${payment.amount}</TableCell>
                                            <TableCell>
                                                <Link component={RouterLink} to={"/admin/users/" + payment.user.id}>
                                                    {payment.user.full_name}
                                                </Link>
                                            </TableCell>
                                            <TableCell>{window.ucFirst(payment.method)}</TableCell>
                                            <TableCell>{(payment.failure_message && payment.failure_message != "") ?  payment.failure_message : payment.description}</TableCell>
                                            <TableCell>
                                                <IconButton
                                                    aria-label="Edit Payment"
                                                    onClick={() => openEditPaymentDialog(payment)}
                                                    variant="contained"
                                                    >
                                                    <EditIcon />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </Paper>
                    }
                    {(invoice.refunds.length > 0) &&
                        <Paper className={classes.paper}>
                            <Grid container spacing={3}>
                                <Grid item className={classes.flexGrow}>
                                    <Typography component="div" variant="h6">
                                        Refunds
                                    </Typography>
                                </Grid>
                                <Grid item>
                                    <Typography component="div" variant="subtitle1">
                                        <Typography component="span" color="textSecondary">
                                            Total Refunded:&nbsp;
                                        </Typography>
                                        ${invoice.amount_refunded_f}
                                    </Typography>
                                </Grid>
                            </Grid>
                            <Table aria-label="Invoice Refunds">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Status</TableCell>
                                        <TableCell>Date</TableCell>
                                        <TableCell align="right">Amount</TableCell>
                                        <TableCell>Refunded To</TableCell>
                                        <TableCell>Method</TableCell>
                                        <TableCell>Message</TableCell>
                                        <TableCell>Delete</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {invoice.refunds.map((refund) => (
                                        <TableRow key={refund.id}>
                                            <TableCell>{window.ucFirst(refund.status)}</TableCell>
                                            <TableCell>
                                                {DateTime.fromISO(refund.created_at).toLocaleString(DateTime.DATE_MED)}
                                            </TableCell>
                                            <TableCell align="right">${refund.amount}</TableCell>
                                            <TableCell>
                                                <Link component={RouterLink} to={"/admin/users/" + refund.user.id}>
                                                    {refund.user.full_name}
                                                </Link>
                                            </TableCell>
                                            <TableCell>{refund.refund_id ? 'Card' : 'Check'}</TableCell>
                                            <TableCell>{refund.failure_message}</TableCell>
                                            <TableCell>
                                                <IconButton
                                                    aria-label="Delete Refund"
                                                    onClick={() => deleteRefund(refund.id)}
                                                    variant="contained"
                                                    >
                                                    <DeleteIcon />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </Paper>
                    }
                    <Grid container
                        alignItems="center"
                        justify="space-between"
                        spacing={3}
                        className={classes.marginTop}
                        >
                        <Grid item>
                        </Grid>
                        <Grid item>

                        </Grid>
                    </Grid>
                    <ChargePaymentMethodDialog
                        user={invoice.user}
                        club={app.club}
                        invoice={invoice}
                        urlPrefix={`/admin/user/${invoice.user_id}`}
                        open={chargePaymentMethodDialogOpen}
                        onClose={closeChargePaymentMethodDialog}
                        onCharge={closeChargePaymentMethodDialogAndRefresh}
                        canUseTerminal
                    />
                    <EditPaymentDialog
                        invoice={invoice}
                        payment={editingPayment}
                        open={editPaymentDialogOpen}
                        onSave={closeEditPaymentDialogAndRefresh}
                        onClose={closeEditPaymentDialog}
                    />
                    <EditRefundDialog
                        invoice={invoice}
                        refund={editingPayment}
                        open={editRefundDialogOpen}
                        onClose={closeEditRefundDialog}
                        onSave={closeEditRefundDialogAndRefresh}
                    />
                    <Dialog
                        open={deleteInvoiceDialogOpen}
                        onClose={closeDeleteInvoiceDialog}
                        aria-labelledby="delete-invoice-title"
                        aria-describedby="delete-invoice-description"
                        fullWidth
                        maxWidth="sm"
                        >
                        <DialogTitle id="delete-invoice-title">Delete Invoice</DialogTitle>
                        <DialogContent>
                            <DialogContentText id="delete-invoice-description">
                                Are you sure you want to delete this invoice? Any payments or refunds connected to a credit card will keep this invoice from deleting.
                            </DialogContentText>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={closeDeleteInvoiceDialog} color="primary" autoFocus>
                                Cancel
                            </Button>
                            <RedButton onClick={deleteInvoice} variant="contained">
                                Delete
                            </RedButton>
                        </DialogActions>
                    </Dialog>
                    <Dialog
                        open={emailDialogOpen}
                        onClose={closeEmailDialog}
                        aria-labelledby="email-dialog-title"
                        aria-describedby="email-dialog-description"
                        fullWidth
                        maxWidth="sm"
                        >
                        <DialogTitle id="email-dialog-title">Email {ucFirst(emailDialogType)}</DialogTitle>
                        <DialogContent>
                            <TextField
                                id="email-address"
                                label="Email Address"
                                value={emailDialogAddress}
                                onChange={handleEmailDialogAddress}
                                fullWidth
                                variant='outlined'
                                helperText="Enter the email address you would like to use."
                            />
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={closeEmailDialog} color="primary" autoFocus>
                                Cancel
                            </Button>
                            <Button onClick={postSendEmail} variant="contained" color="primary">
                                Send
                            </Button>
                        </DialogActions>
                    </Dialog>
                </Fragment>
            }
        </form>
    )
}
